import { useCallback, FC } from 'react';

import { DATE_FORMAT_ISO, parse } from '@eneco/common/date';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { productUnits } from '@eneco/enumerations/productUnits';
import { DetailsIcon } from '@eneco/flows/components';
import { DetailsIconType } from '@eneco/flows/types';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useFormatter } from '@eneco/i18n';
import { Box, Stack, Text, TextLink, Divider, Popover, TabSwitch } from '@eneco/sparky';
import { InfoIcon } from '@eneco/sparky/icons';
import { DC_Domain_Models_Products_ProductType } from '@monorepo-types/dc';
import { DialogField } from '@sitecore/types/OfferStep';
import { UnitPriceLabels, ProductData } from '@sitecore/types/SMEOfferStep';
import { Fields as SummaryStepFields } from '@sitecore/types/SMESummaryStep';
import { TextField } from '@sitecore-jss/sitecore-jss-react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { PriceDetailsOverview } from '../../components';
import { FlowContextSme } from '../../context';
import { getProductTitleFromProductType, constructTextualDataKey } from '../../helpers';
import { Offer, Product } from '../../types';

interface ProductDetails {
  title?: string;
  promotionText?: string;
  tariffDescription?: string;
  tariffSplitExplanation?: string;
  usps: string[];
  unitPriceLabels?: ProductUnitPriceLabels;
  tagLabel?: string;
  endOfContractInformationDialog: DialogField;
  electricityDeliveryRateLabel?: TextField;
  gasDeliveryRateLabel?: TextField;
}

interface ProductUnitPriceLabels {
  electricityHighLabel?: string;
  electricityLabel?: string;
  electricityLowLabel?: string;
  gasLabel?: string;
  redeliveryLabel?: string;
  warmthLabel?: string;
  waterColdLabel?: string;
  waterWarmLabel?: string;
}

export interface Data {
  byPeriodLabel: TextField;
  id: TextField;
  usp2Text: TextField;
  usp1Text: TextField;
  usp3Text: TextField;
  usp4Text: TextField;
  name: TextField;
  promotionText: TextField;
  tariffDescriptionText?: TextField;
  tariffSplitExplanationText?: TextField;
  endOfContractInformationDialog: DialogField;
  electricityDeliveryRateLabel?: TextField;
  gasDeliveryRateLabel?: TextField;
}
interface OfferData {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    data?: Data;
    unitPriceLabels?: {
      electricityHighLabel?: TextField;
      electricityLabel?: TextField;
      electricityLowLabel?: TextField;
      gasLabel?: TextField;
      redeliveryLabel?: TextField;
      warmthLabel?: TextField;
      waterColdLabel?: TextField;
      waterWarmLabel?: TextField;
      tagLabel?: TextField;
    };
  };
  [k: string]: unknown;
}

export const useProductItemContent = (textualData?: OfferData[]) => {
  const doesItemExist = useCallback(
    (key: string) => {
      if (!textualData) {
        return false;
      }
      return textualData?.some(i => i.fields?.data?.id?.value === key);
    },
    [textualData],
  );

  const getProductDetails: (key: string) => ProductDetails | null = useCallback(
    (key: string) => {
      // Check for existence for safety, in case consumer doesn't check using 'doesItemExist'
      if (!doesItemExist(key)) {
        return null;
      }

      const productItem = textualData!.find(i => i.fields?.data?.id?.value === key);

      // Unwrap values
      return {
        title: productItem?.fields?.data?.name?.value,
        promotionText: productItem?.fields?.data?.promotionText?.value,
        tariffDescription: productItem?.fields?.data?.tariffDescriptionText?.value,
        tariffSplitExplanation: productItem?.fields?.data?.tariffSplitExplanationText?.value,
        endOfContractInformationDialog: productItem?.fields?.data?.endOfContractInformationDialog,
        electricityDeliveryRateLabel: productItem?.fields?.data?.electricityDeliveryRateLabel,
        gasDeliveryRateLabel: productItem?.fields?.data?.gasDeliveryRateLabel,
        unitPriceLabels: {
          electricityLabel: productItem?.fields?.unitPriceLabels?.electricityLabel?.value || undefined,
          electricityHighLabel: productItem?.fields?.unitPriceLabels?.electricityHighLabel?.value || undefined,
          electricityLowLabel: productItem?.fields?.unitPriceLabels?.electricityLowLabel?.value || undefined,
          gasLabel: productItem?.fields?.unitPriceLabels?.gasLabel?.value || undefined,
          warmthLabel: productItem?.fields?.unitPriceLabels?.warmthLabel?.value || undefined,
          redeliveryLabel: productItem?.fields?.unitPriceLabels?.waterWarmLabel?.value || undefined,
          waterColdLabel: productItem?.fields?.unitPriceLabels?.waterColdLabel?.value || undefined,
          waterWarmLabel: productItem?.fields?.unitPriceLabels?.waterWarmLabel?.value || undefined,
        },
        tagLabel: productItem?.fields?.unitPriceLabels?.tagLabel?.value || undefined,
        usps: !productItem?.fields?.data
          ? []
          : Object.entries(productItem.fields.data)
              .filter(([key, _]) => {
                return key.startsWith('usp');
              })
              .filter(([_, value]) => Boolean(value.value))
              .map(([_, value]) => value.value),
      };
    },
    [doesItemExist, textualData],
  ) as (key: string) => ProductDetails | null;

  return { doesItemExist, getProductDetails };
};

type OfferFields = {
  unitPriceLabels: UnitPriceLabels;
  productData: ProductData & {
    electricityDisclosureText: { value: string };
    gasDisclosureText: { value: string };
  };
};

export type MergedFields = OfferFields & SummaryStepFields;

export const ProductsPricingBreakdown: FC<{
  offer: Offer;
  textualData?: MergedFields;
  contractStartDateLabel?: string;
  contractEndDateLabel?: string;
}> = ({ textualData, offer, contractStartDateLabel, contractEndDateLabel }) => {
  const formMethods = useForm<{ costPeriod: string }>({
    mode: 'onBlur',
    defaultValues: {
      costPeriod: 'costsMonthly',
    },
  });
  const { control } = formMethods;
  const { currency, date } = useFormatter();
  const { costPeriod = 'costsMonthly' } = useWatch({ control });
  const { useFlowSelector } = useFlowHooks<FlowContextSme>();
  const flowContext = useFlowSelector(state => state.context);
  const { energyType } = flowContext;
  type CostPeriod = 'costsMonthly' | 'costsYearly';

  const periodLabel: Record<CostPeriod, string> = {
    costsMonthly: 'per maand',
    costsYearly: 'per jaar',
  };

  const { getProductDetails } = useProductItemContent((textualData?.productData?.productDataList ?? []) as OfferData[]);
  const productDetails = getProductDetails(constructTextualDataKey(offer?.contractSpan, energyType));

  const getEnergyDisclosurePopoverData = (productType: DC_Domain_Models_Products_ProductType) => {
    if (productType === 'electricity') {
      return textualData?.productData?.electricityDisclosureText;
    }

    if (productType === 'gas') {
      return textualData?.productData?.gasDisclosureText;
    }

    return null;
  };
  const { costDetails } = offer || {};

  const getCostPerPeriod = (data: Offer | Product) =>
    costPeriod === 'costsMonthly' ? data.costsMonthly : data.costsYearly;
  const label = periodLabel[(costPeriod ?? 'costsMonthly') as 'costsMonthly' | 'costsYearly'];
  const getEnergyLabel = (productType: DC_Domain_Models_Products_ProductType) => {
    const electricityHigh = currency.euroHighPrecision(
      costDetails?.electricityCostDetails?.tariff?.price?.vatExcluded ?? 0,
    );
    const electricityLow = currency.euroHighPrecision(
      costDetails?.electricityCostDetails?.lowtariff?.price?.vatExcluded ?? 0,
    );
    const gasPrice = currency.euroHighPrecision(costDetails?.gasCostDetails?.tariff?.price?.vatExcluded ?? 0);

    const normal = `${electricityHigh} / ${productUnits.electricity}`;
    const dal = `${electricityLow} / ${productUnits.electricity}`;
    const gas = `${gasPrice} / ${productUnits.gas}`;

    if (productType === 'electricity') {
      if (flowContext?.hasDoubleMeter) {
        const doubleMeterLabel =
          `<br />${productDetails?.unitPriceLabels?.electricityHighLabel || textualData?.unitPriceLabels?.electricityHighLabel.value} ${normal} ` +
          `<br />${productDetails?.unitPriceLabels?.electricityLowLabel || textualData?.unitPriceLabels?.electricityLowLabel.value} ${dal}`;

        return productDetails?.electricityDeliveryRateLabel?.value?.toString()?.replace('{value}', doubleMeterLabel);
      }

      const electricityLabel = `<br />${productDetails?.unitPriceLabels?.electricityLabel || textualData?.unitPriceLabels?.electricityLabel.value} ${normal}`;
      return productDetails?.electricityDeliveryRateLabel?.value?.toString()?.replace('{value}', electricityLabel);
    }

    if (productType === 'gas') {
      const gasLabel = `<br />${productDetails?.unitPriceLabels?.gasLabel || textualData?.unitPriceLabels?.gasLabel?.value} ${gas}`;
      return productDetails?.gasDeliveryRateLabel?.value?.toString()?.replace('{value}', gasLabel);
    }
  };

  const priceTypeToggle = (
    <Controller
      control={control}
      name="costPeriod"
      render={({ field: { onChange, value, name } }) => (
        <TabSwitch
          name={name}
          value={value}
          onValueChange={val => {
            onChange(val);
          }}>
          <TabSwitch.Item key="costsMonthly" value="costsMonthly">
            Prijs per maand
          </TabSwitch.Item>

          <TabSwitch.Item key="costsYearly" value="costsYearly">
            Prijs per jaar
          </TabSwitch.Item>
        </TabSwitch>
      )}
    />
  );

  const pricePerProduct = (
    <Stack gap="5">
      {offer.products.map(product => {
        const energyDisclosureData = getEnergyDisclosurePopoverData(product.type);

        return (
          <Stack gap="2" key={product.code}>
            <Stack direction="row" alignY="start" gap="2">
              <Stack.Item grow>
                <Stack direction="row" gap="2" alignY="start">
                  <DetailsIcon type={product.type as DetailsIconType} />
                  <Stack direction="column" alignY="center">
                    <Stack direction="row" gap={2}>
                      <Text weight="bold">
                        {getProductTitleFromProductType(product.type, textualData?.productTypeLabels)}
                      </Text>
                      {energyDisclosureData?.value && (
                        <Popover
                          side="bottom"
                          size="small"
                          title={energyDisclosureData?.value}
                          trigger={
                            <TextLink
                              leftIcon={<InfoIcon size="small" verticalAlign="text-top" color={'currentColor'} />}>
                              {''}
                            </TextLink>
                          }>
                          <Text weight="regular">
                            <RichText html={energyDisclosureData?.value} />
                          </Text>
                        </Popover>
                      )}
                    </Stack>
                    <Text weight="regular">{product.title}</Text>
                  </Stack>
                </Stack>
              </Stack.Item>
              <Stack.Item shrink={false}>
                <Stack direction="column" alignX="end" gap="0">
                  <Text weight="bold">{currency.euroHighPrecision(getCostPerPeriod(product)?.vatExcluded ?? 0)}</Text>
                  <Text size="BodyS">{label}</Text>
                </Stack>
              </Stack.Item>
            </Stack>
            <Divider />
            {getEnergyLabel(product.type) && (
              <Box paddingLeft={8} paddingRight={4}>
                <Text size="BodyS" color="textLowEmphasis">
                  <RichText html={getEnergyLabel(product.type)} />
                </Text>
              </Box>
            )}
          </Stack>
        );
      })}
    </Stack>
  );

  const totalProductsPrice = (
    <Stack direction="row" alignY="start" gap="2">
      <Stack.Item grow={true}>
        <Stack direction="row" gap="2">
          <Text weight="bold">Totaal</Text>
        </Stack>
      </Stack.Item>

      <Stack.Item shrink={false}>
        <Stack direction="column" alignY="end" gap="2">
          <Stack direction="column" alignY="end" alignX="end">
            <Stack.Item grow={true}>
              <Text weight="bold">{currency.euroHighPrecision(getCostPerPeriod(offer)?.vatExcluded ?? 0)}</Text>
            </Stack.Item>
            <Stack.Item grow={true}>
              <Text size="BodyS">{label}</Text>
            </Stack.Item>
          </Stack>
        </Stack>
      </Stack.Item>
    </Stack>
  );

  const endOfContractInformation = productDetails?.endOfContractInformationDialog?.value?.triggerText ? (
    <Stack>
      <Text size="BodyS">
        <TrackedDialog
          title={productDetails?.endOfContractInformationDialog?.value?.title}
          trigger={
            <TextLink emphasis="low">{productDetails?.endOfContractInformationDialog?.value?.triggerText}</TextLink>
          }>
          <RichText html={productDetails?.endOfContractInformationDialog?.value?.content} />
        </TrackedDialog>
      </Text>
    </Stack>
  ) : null;

  const contractDates = contractStartDateLabel ? (
    <Stack direction="column" gap="1">
      <Stack direction="row" gap="1" alignX="justify">
        <Text size="BodyS">{contractStartDateLabel}</Text>
        <Text size="BodyS">
          {offer?.contractStartDate && date.short(parse(offer.contractStartDate, DATE_FORMAT_ISO))}
        </Text>
      </Stack>

      {offer?.contractEndDate && contractEndDateLabel && (
        <Stack direction="row" gap="1" alignX="justify">
          <Text size="BodyS">{contractEndDateLabel}</Text>
          <Text size="BodyS">{date.short(parse(offer.contractEndDate, DATE_FORMAT_ISO))}</Text>
        </Stack>
      )}
    </Stack>
  ) : null;

  const priceStructure = (
    <Text size="BodyM">
      <TrackedDialog title="Bekijk de prijsopbouw" trigger={<TextLink emphasis="high">Bekijk de prijsopbouw</TextLink>}>
        <PriceDetailsOverview offer={offer} flowContext={flowContext ?? {}} textualData={textualData} />
      </TrackedDialog>
    </Text>
  );

  return (
    <Stack gap="6">
      {priceTypeToggle}
      <Stack gap="6">
        {pricePerProduct}
        {totalProductsPrice}
        <Stack gap="4">
          <Divider />
          {contractDates}
          {endOfContractInformation}
        </Stack>
        {priceStructure}
        {textualData?.salesConditions?.content?.value && (
          <Text size="BodyXS">
            <RichText html={textualData?.salesConditions?.content?.value}></RichText>
          </Text>
        )}
      </Stack>
    </Stack>
  );
};
